import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, ChevronDown, ChevronUp, HelpCircle, Loader2 } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import axios from 'axios';
import { API } from '@/App';

const FAQPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [openIndex, setOpenIndex] = useState(null);
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const res = await axios.get(`${API}/faq`);
        setFaqItems(res.data.filter(f => f.is_active !== false));
      } catch { /* fallback */ }
      setLoading(false);
    };
    fetchFaqs();
  }, []);

  // Group FAQs by category
  const categories = {};
  faqItems.forEach(f => {
    const cat = f.category || 'Général';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(f);
  });

  const filteredCategories = {};
  Object.entries(categories).forEach(([cat, questions]) => {
    const filtered = questions.filter(q =>
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) filteredCategories[cat] = filtered;
  });

  // Flat index for open/close
  let globalIdx = 0;
  const allItems = [];
  Object.entries(filteredCategories).forEach(([cat, questions]) => {
    questions.forEach(q => {
      allItems.push({ ...q, globalIdx: globalIdx++, category: cat });
    });
  });

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <SiteHeader />
      <SEO title="FAQ" description="Questions frequemment posees sur la SFOALC, l'adhesion, les formations et les événements." />
      <div className="h-[72px]" />

      {/* Hero */}
      <section className="pt-12 pb-10 px-4" style={{ background: '#eaf6fc' }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-[#1e3a5f] flex items-center justify-center mx-auto mb-5">
            <HelpCircle className="text-white" size={30} />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="faq-title">
            Questions frequentes
          </h1>
          <p className="text-gray-600 mb-8">Trouvez rapidement les réponses a vos questions</p>

          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Rechercher une question..."
              className="pl-11 py-3 rounded-xl border-gray-200 bg-white shadow-sm"
              data-testid="faq-search"
            />
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#35AFE4]" size={28} /></div>
          ) : Object.keys(filteredCategories).length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Aucun resultat pour "{searchTerm}"</p>
              <Button onClick={() => setSearchTerm('')} variant="outline" className="mt-4">Effacer la recherche</Button>
            </div>
          ) : (
            Object.entries(filteredCategories).map(([cat, questions]) => (
              <div key={cat} className="mb-8" data-testid={`faq-category-${cat}`}>
                <h2 className="text-lg font-bold text-[#1e3a5f] mb-4 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  <div className="w-2 h-2 rounded-full bg-[#35AFE4]" />
                  {cat}
                </h2>
                <div className="space-y-2">
                  {questions.map(q => {
                    const item = allItems.find(a => a.id === q.id);
                    const idx = item?.globalIdx ?? 0;
                    const isOpen = openIndex === idx;
                    return (
                      <div key={q.id} className="border border-gray-100 rounded-xl overflow-hidden hover:border-gray-200 transition-all" data-testid={`faq-item-${q.id}`}>
                        <button
                          onClick={() => setOpenIndex(isOpen ? null : idx)}
                          className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 bg-white hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-gray-800 text-sm">{q.question}</span>
                          {isOpen ? <ChevronUp size={18} className="text-[#35AFE4] flex-shrink-0" /> : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-4 pt-0 bg-gray-50 border-t border-gray-100">
                            <p className="text-gray-600 text-sm leading-relaxed">{q.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4 bg-gray-50 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>
            Vous n'avez pas trouve votre réponse ?
          </h2>
          <p className="text-gray-500 mb-6">Notre équipe est disponible pour vous aider</p>
          <Button onClick={() => navigate('/contact')} className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white font-semibold px-8 py-3 rounded-xl" data-testid="faq-contact-btn">
            Contactez-nous
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
